import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientService } from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, KeyRound } from 'lucide-react';

function ClientLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agencyId = searchParams.get('agency') ?? '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!agencyId) {
      setError('Enlace de acceso inválido. Solicita uno nuevo a tu asesor.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await clientService.login(agencyId, email, password);
      localStorage.setItem('client_access_token', data.access_token);
      navigate('/portal');
    } catch {
      setError('Credenciales incorrectas. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background flex">
      {/* Left panel */}
      <div
        className="hidden lg:flex lg:w-[52%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'radial-gradient(ellipse at 20% 80%, #263329 0%, #0E1510 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-semibold tracking-tight">Takarah</span>
          </div>
        </div>
        <div className="relative z-10">
          <h2 className="text-3xl font-semibold text-white leading-snug mb-3">
            Tu portal de propiedades<br />personales
          </h2>
          <p className="text-white/50 text-sm leading-relaxed max-w-xs">
            Accede a la selección que tu asesor preparó para ti. Marca favoritos, revisa detalles y coordina visitas.
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Building2 className="w-5 h-5 text-primary" />
            <span className="font-semibold">Takarah</span>
          </div>

          <div className="mb-8">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <KeyRound className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Portal de cliente</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Ingresa con tus credenciales de acceso
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                autoComplete="email"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                required
                autoComplete="current-password"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button
              type="submit"
              className="w-full transition-all active:scale-[0.98]"
              disabled={loading || !agencyId}
            >
              {loading ? 'Ingresando...' : 'Ingresar al portal'}
            </Button>

            {!agencyId && (
              <p className="text-xs text-muted-foreground text-center">
                Este enlace no es válido. Pide a tu asesor que te envíe uno nuevo.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default ClientLoginPage;
